<template>
  <div>
    <div class="pb-baseline2">{{ t('motorCockpitForm.common.titleText') }}</div>
    <Alert class="mb-2" :text="$t('motorCockpitForm.common.infoText')"></Alert>
    <div v-for="(form, index) in forms" :key="index">
      <div>
        <div v-if="form === 'ChangeFahrleistung'" :id="'component-' + form">
          <div class="pb-baseline">
            <SwitchButton
              @input="updateFormMileageChecked"
              :textChecked="$t('motorCockpitForm.mileage.sliderText')"
              :textUnchecked="$t('motorCockpitForm.mileage.sliderText')"
              :value="formMileageChecked"
              color="primary"
            ></SwitchButton>
          </div>
          <div v-if="formMileageChecked" class="form">
            <ChangeFahrleistung
              :key="formId"
              v-model:value="requestNew.mileage"
              :motorContract="motorDetails.motorContract"
              @newMileageStatus="newMileageStatusEvent"
              @status="mileageStatusEvent"
            ></ChangeFahrleistung>
          </div>
        </div>
        <div v-if="form === 'ChangeFahrerkreis'" :id="'component-' + form">
          <div class="pb-baseline">
            <SwitchButton
              @input="updateCircleOfDriversChecked"
              :textChecked="$t('motorCockpitForm.circleOfDrivers.sliderText')"
              :textUnchecked="$t('motorCockpitForm.circleOfDrivers.sliderText')"
              :value="formCircleOfDriversChecked"
              color="primary"
            ></SwitchButton>
          </div>
          <div v-if="formCircleOfDriversChecked" class="form">
            <ChangeFahrerkreis
              v-model:value="requestNew.circleOfDrivers"
              :motorContract="motorDetails.motorContract"
              @status="circleOfDriversStatusEvent"
            ></ChangeFahrerkreis>
          </div>
        </div>
        <div
          v-if="
            form === 'ChangeDeckungsumfang' && getIsVersicherungsumfangChangeAllowed()
          "
          :id="'component-' + form"
        >
          <div class="pb-baseline">
            <SwitchButton
              @input="updateFormInsuranceCoverChecked"
              :textChecked="$t('motorCockpitForm.insuranceCoverGroup.sliderText')"
              :textUnchecked="$t('motorCockpitForm.insuranceCoverGroup.sliderText')"
              :value="formInsuranceCoverChecked"
              color="primary"
            ></SwitchButton>
          </div>
          <div v-if="formInsuranceCoverChecked" class="form">
            <ChangeDeckungsumfang
              :key="formId"
              :motorContract="motorDetails.motorContract"
              :value="requestNew.insuranceCover"
              :vehicleAge="motorDetails.vehicleAge"
              @extrasChanged="extrasChanged"
              @status="insuranceCoverStatusEvent"
            ></ChangeDeckungsumfang>
          </div>
        </div>
      </div>
    </div>
    <div v-if="oneSliderActivated">
      <div class="card-no-padding">
        <Card :title="$t('motorCockpitForm.common.tableHeader')"></Card>
      </div>
      <div class="pb-baseline2 change-table">
        <DataTable :data="getDataTableRows()" :header="getDataTableHeader()"></DataTable>
      </div>
    </div>
    <div v-if="!oneSliderActivated" class="form-group__error">
      {{ $t('motorCockpitForm.common.noSliderActivated') }}
    </div>
    <div class="margin-calculate pb-baseline">
      <div class="card-no-padding">
        <CalculatePremium
          :isCalculateButtonEnabled="isCalculateButtonEnabled"
          :modal="modal"
          :motorContractCoverageChangeRequest="getMotorContractCoverageChangeRequest()"
          :paymentPeriod="motorDetails.motorContract.paymentData.paymentPeriod"
          :premiumSubtext="premiumSubtext"
          :showRecalculateButton="showRecalculateButton"
          @resetData="resetData"
          @updatePremium="updatePremium"
        ></CalculatePremium>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  Alert,
  Card,
  DataTable,
  type HeaderConfig,
  InputStatus,
  SwitchButton,
  WidthType
} from 'de-zurich-ui-library'
import { computed, onBeforeMount, type PropType, type Ref, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { type PolicyMotorDetails } from '@/types/motor/PolicyMotorDetails'
import { partnerProxyIdReg } from '@/utilsTenant/PropsValidation'
import {
  ChangeFormEnum,
  deductibleToString,
  type Fahrleistung,
  type InsuranceCover,
  type MotorContractCoverageChange,
  type MotorContractCoverageChangeRequest
} from '@/typesTenant/MotorContractChange'
import {
  areHaftpflichtExtrasEqual,
  areKaskoExtrasEqual,
  areTwoMotorContractsEqual,
  areTwoObjectsWithouthGetterSettersEqual,
  checkIfAtleastOneKaskoExtraIsChecked,
  formatKilometer,
  getCircleOfDrivers,
  getInsuranceCover,
  isVersicherungsumfangChangeAllowed,
  removeGettersSetters
} from '@/utilsTenant/MotorUtils'
import dayjs from 'dayjs'
import type { HaftpflichtExtras, KaskoExtras } from '@/types/policy/PolicyTypes'
import CalculatePremium from '@/componentsTenant/motor/cockpit/CalculatePremium.vue'
import ChangeFahrleistung from '@/componentsTenant/motor/cockpit/ChangeFahrleistung.vue'
import ChangeFahrerkreis from '@/componentsTenant/motor/cockpit/ChangeFahrerkreis.vue'
import ChangeDeckungsumfang from '@/componentsTenant/motor/cockpit/ChangeDeckungsumfang.vue'
import { submitMotorContractChange } from '@/storeTenant/motorServiceTenant'

const { t } = useI18n()

const props = defineProps({
  premiumSubtext: {
    type: String,
    required: true
  },
  modal: {
    type: Object as PropType<Record<string, string>>,
    required: true
  },
  isProlongationContract: {
    type: Boolean,
    required: false,
    default: false
  },
  changeType: {
    type: String,
    required: false,
    default: ''
  },
  motorDetails: {
    type: Object as PropType<PolicyMotorDetails>,
    required: true
  },
  partnerProxyId: {
    type: String,
    required: true,
    validator(value: string): boolean {
      return partnerProxyIdReg.test(value)
    }
  }
})

const emit = defineEmits<{
  formsValid: [value: boolean]
  formSubmitting: [value: boolean]
  formShowResponse: [value1: boolean, value2: boolean]
}>()

const insuranceCover: Ref<InsuranceCover> = ref(
  getInsuranceCover(props.motorDetails.motorContract)
)
const mileage: Fahrleistung = {
  currentKmStanding: 0,
  newMileage: parseInt(props.motorDetails.motorContract.insuredVehicle.insuredMileage, 10)
}
const circleOfDrivers = getCircleOfDrivers(props.motorDetails.motorContract)

const requestOld = ref(
  Object.seal(
    JSON.parse(
      JSON.stringify({
        insuranceCover: { ...insuranceCover.value },
        mileage: { ...mileage },
        circleOfDrivers: { ...circleOfDrivers }
      })
    )
  )
)
const requestNew = ref(JSON.parse(JSON.stringify(requestOld.value)))

const requestCalculated = ref(
  JSON.parse(
    JSON.stringify({
      insuranceCover: { ...insuranceCover.value },
      mileage: { ...mileage },
      circleOfDrivers: { ...circleOfDrivers }
    })
  )
)
const premium = ref(0)

// Forms
const forms: string[] = [
  'ChangeFahrleistung',
  'ChangeFahrerkreis',
  'ChangeDeckungsumfang'
]
const formMileageChecked = ref(false)
const formCircleOfDriversChecked = ref(false)
const formInsuranceCoverChecked = ref(false)

// Form Status
const mileageStatus = ref<InputStatus>(InputStatus.DEFAULT)
const circleOfDriversStatus = ref<InputStatus>(InputStatus.DEFAULT)
const insuranceCoverStatus = ref<InputStatus>(InputStatus.DEFAULT)
const newMileageInputStatus = ref<InputStatus>(InputStatus.DEFAULT)

// This variable is used to reset the forms after incrementing it
const formId = ref(0)
const insuranceCoverStateChanged = ref(false)
const insuranceCoverStateCounter = ref(0)
const didCalculationDataChange = ref(true)
const numberOfFormsChecked = ref()

onBeforeMount(async () => {
  numberOfFormsChecked.value = getHowManyFormChecked()
  if (forms)
    forms.forEach((form: any, index: number) => {
      if (form === props.changeType) {
        forms.splice(index, 1)
        forms.unshift(form)
      }
    })

  if (props.changeType === ChangeFormEnum.ChangeFahrleistung) {
    formMileageChecked.value = true
  }
  if (props.changeType === ChangeFormEnum.ChangeFahrerkreis) {
    formCircleOfDriversChecked.value = true
  }
  if (props.changeType === ChangeFormEnum.ChangeVersicherungsumfang) {
    formInsuranceCoverChecked.value = true
  }

  scrollToTop()
})

watch(
  () => formCircleOfDriversChecked.value,
  () => {
    resetCircleOfDriversForm()
  }
)

watch(
  () => formMileageChecked.value,
  () => {
    resetMileageForm()
  }
)

watch(
  () => formInsuranceCoverChecked.value,
  () => {
    resetInsuranceCoverForm()
  }
)

watch(
  () => [premium.value, formsValid()],
  () => {
    formsValidAndCalculated()
  }
)

watch(
  () => formInsuranceCoverChecked.value,
  () => {
    showRecalculateButtonWhenInsuranceCoverIsChecked()
  }
)

function resetCircleOfDriversForm(): void {
  if (!formCircleOfDriversChecked.value) {
    requestNew.value.circleOfDrivers = JSON.parse(
      JSON.stringify(requestOld.value.circleOfDrivers)
    )
    circleOfDriversStatus.value = InputStatus.DEFAULT
  }
}

function resetMileageForm(): void {
  if (!formMileageChecked.value) {
    requestNew.value.mileage = JSON.parse(JSON.stringify(requestOld.value.mileage))
    mileageStatus.value = InputStatus.DEFAULT
  }
}

function resetInsuranceCoverForm(): void {
  if (!formInsuranceCoverChecked.value) {
    requestNew.value.insuranceCover = JSON.parse(
      JSON.stringify(requestOld.value.insuranceCover)
    )
    insuranceCoverStatus.value = InputStatus.DEFAULT
  }
}

function showRecalculateButtonWhenInsuranceCoverIsChecked(): void {
  if (formInsuranceCoverChecked.value) {
    insuranceCoverStateChanged.value = true
    insuranceCoverStateCounter.value = 2
  }
}

function formsValidAndCalculated(): boolean {
  const formsValidAndCalculated =
    formsValid() && !formsRecalculateNeeded.value && !showRecalculateButton.value
  emit('formsValid', formsValidAndCalculated)
  return formsValidAndCalculated
}

const isCalculateButtonEnabled = computed(() => {
  return dataChanged.value && formsValid()
})

const dataChanged = computed(() => {
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  didCalculationDataChange.value = true
  return !areTwoMotorContractsEqual(requestOld.value, requestNew.value)
})

const formsRecalculateNeeded = computed(() => {
  const didFormDataChange = !areTwoMotorContractsEqual(
    requestCalculated.value,
    requestNew.value
  )

  return didFormDataChange || didCalculationDataChange.value
})

function getDataTableHeader(): HeaderConfig[] {
  return [
    { name: '', width: { size: 224, type: WidthType.PIXEL } },
    { name: 'bisher' },
    { name: 'neu' }
  ]
}

const oneSliderActivated = computed(() => {
  return (
    formMileageChecked.value ||
    formCircleOfDriversChecked.value ||
    formInsuranceCoverChecked.value
  )
})

function getIsVersicherungsumfangChangeAllowed(): boolean {
  return isVersicherungsumfangChangeAllowed(props.motorDetails)
}

function getDataTableRows(): string[][] {
  const rows: string[][] = []
  const oldRequest: MotorContractCoverageChange = removeGettersSetters(requestOld.value)
  const newRequest: MotorContractCoverageChange = removeGettersSetters(requestNew.value)

  if (formMileageChecked.value) {
    addMileageRow(rows, oldRequest, newRequest)
  }

  if (formCircleOfDriversChecked.value) {
    addCircleOfDriversRow(rows, oldRequest, newRequest)
  }

  if (formInsuranceCoverChecked.value) {
    addInsuranceCoverRow(rows, oldRequest, newRequest)
    addDeductibleRow(rows, oldRequest, newRequest)
    addLeistungsextrasRow(rows, oldRequest, newRequest)
  }

  return rows
}

function addMileageRow(
  rows: string[][],
  oldRequest: MotorContractCoverageChange,
  newRequest: MotorContractCoverageChange
): void {
  const isNewMileageInputValid = newMileageInputStatus.value === InputStatus.VALID
  const mileageChanged = oldRequest.mileage.newMileage !== newRequest.mileage.newMileage

  rows.push([
    t('motorCockpitForm.mileage.title'),
    formatKilometer(oldRequest.mileage.newMileage),
    mileageChanged && isNewMileageInputValid
      ? formatKilometer(newRequest.mileage.newMileage)
      : 'nicht geändert'
  ])
}

function addCircleOfDriversRow(
  rows: string[][],
  oldRequest: MotorContractCoverageChange,
  newRequest: MotorContractCoverageChange
): void {
  const circleOfDriversChanged =
    oldRequest.circleOfDrivers.circleOfDriversType !==
      newRequest.circleOfDrivers.circleOfDriversType ||
    oldRequest.circleOfDrivers.vehicleUsage !== newRequest.circleOfDrivers.vehicleUsage

  rows.push([
    t('motorCockpitForm.circleOfDrivers.title'),
    t(`circleOfDrivers.values.${oldRequest.circleOfDrivers.circleOfDriversType}`) +
      '\n' +
      formatVehicleUsage(requestOld.value),
    circleOfDriversChanged
      ? t(`circleOfDrivers.values.${newRequest.circleOfDrivers.circleOfDriversType}`) +
        '\n' +
        formatVehicleUsage(newRequest)
      : 'nicht geändert'
  ])
}

function addInsuranceCoverRow(
  rows: string[][],
  oldRequest: MotorContractCoverageChange,
  newRequest: MotorContractCoverageChange
): void {
  const kaskodeckungChanged =
    oldRequest.insuranceCover.comprehensiveCover !==
    newRequest.insuranceCover.comprehensiveCover

  rows.push([
    t('motorCockpitForm.common.insuranceCover'),
    t(`insuranceCover.kaskodeckung.${oldRequest.insuranceCover.comprehensiveCover}`),
    kaskodeckungChanged
      ? t(`insuranceCover.kaskodeckung.${newRequest.insuranceCover.comprehensiveCover}`)
      : 'nicht geändert'
  ])
}

function addDeductibleRow(
  rows: string[][],
  oldRequest: MotorContractCoverageChange,
  newRequest: MotorContractCoverageChange
): void {
  const selbstbeteiligungChanged =
    oldRequest.insuranceCover.deductible.deductiblePartiallyComprehensive !==
      newRequest.insuranceCover.deductible.deductiblePartiallyComprehensive ||
    oldRequest.insuranceCover.deductible.deductibleFullyComprehensive !==
      newRequest.insuranceCover.deductible.deductibleFullyComprehensive

  rows.push([
    t('motorCockpitForm.selbstbeteiligung.title'),
    deductibleToString(oldRequest.insuranceCover.deductible),
    selbstbeteiligungChanged
      ? deductibleToString(newRequest.insuranceCover.deductible)
      : 'nicht geändert'
  ])
}

function addLeistungsextrasRow(
  rows: string[][],
  oldRequest: MotorContractCoverageChange,
  newRequest: MotorContractCoverageChange
): void {
  const leistungsextrasListOld = getLeistungsextrasList(oldRequest)
  const leistungsextrasListNew = getLeistungsextrasList(newRequest)

  const kaskodeckungChanged =
    oldRequest.insuranceCover.comprehensiveCover !==
    newRequest.insuranceCover.comprehensiveCover
  const leistungsextrasChanged = !areExtrasEqual(oldRequest, newRequest)

  let displayValue

  if (kaskodeckungChanged) {
    // Fall 1: Kaskodeckung hat sich geändert
    displayValue =
      leistungsextrasListNew.length !== 0 ? leistungsextrasListNew : 'nicht wählbar'
  } else if (leistungsextrasChanged) {
    // Fall 2: Nur Leistungsextras haben sich geändert
    displayValue = leistungsextrasListNew
  } else {
    // Fall 3: Nichts hat sich geändert
    displayValue = 'nicht geändert'
  }

  rows.push([
    t('motorCockpitForm.leistungsextras.title'),
    leistungsextrasListOld,
    displayValue
  ])
}

function getLeistungsextrasList(request: MotorContractCoverageChange): string {
  const haftpflichtExtras = getHaftpflichtLeistungsExtras(
    request.insuranceCover.newExtras.liabilityProduct.extras
  )

  const kaskoExtras = getKaskoLeistungsExtras(
    request.insuranceCover.newExtras.comprehensiveInsuranceProduct?.extras
  )

  return [...haftpflichtExtras, ...kaskoExtras].join('\n')
}

function areExtrasEqual(
  oldRequest: MotorContractCoverageChange,
  newRequest: MotorContractCoverageChange
): boolean {
  const leistungsextraHaftpflichtEqual = areHaftpflichtExtrasEqual(
    oldRequest.insuranceCover.newExtras.liabilityProduct.extras,
    newRequest.insuranceCover.newExtras.liabilityProduct.extras
  )

  let leistungsextraKaskoEqual = true
  if (
    oldRequest.insuranceCover.newExtras.comprehensiveInsuranceProduct &&
    newRequest.insuranceCover.newExtras.comprehensiveInsuranceProduct
  ) {
    leistungsextraKaskoEqual = areKaskoExtrasEqual(
      oldRequest.insuranceCover.newExtras.comprehensiveInsuranceProduct.extras,
      newRequest.insuranceCover.newExtras.comprehensiveInsuranceProduct.extras
    )
  } else if (
    oldRequest.insuranceCover.newExtras.comprehensiveInsuranceProduct === undefined &&
    newRequest.insuranceCover.newExtras.comprehensiveInsuranceProduct
  ) {
    leistungsextraKaskoEqual = !checkIfAtleastOneKaskoExtraIsChecked(
      newRequest.insuranceCover.newExtras.comprehensiveInsuranceProduct
    )
  }

  return leistungsextraHaftpflichtEqual && leistungsextraKaskoEqual
}

function getMotorContractCoverageChangeRequest(): MotorContractCoverageChangeRequest {
  return {
    policyId:
      props.motorDetails.motorContract.contractBase.companyCode +
      '-' +
      props.motorDetails.motorContract.contractBase.policyNumber,
    dateOfChange: dayjs().add(1, 'day').format('YYYYMMDD'),
    calculatedPrice: premium.value,
    circleOfDrivers: formCircleOfDriversChecked.value
      ? requestNew.value.circleOfDrivers
      : null,
    mileage: formMileageChecked.value ? requestNew.value.mileage : null,
    insuranceCover: getInsuranceCoverRequestData(),
    isProlongation: props.isProlongationContract
  }
}

function formsValid(): boolean {
  const mileageValid = formMileageChecked.value
    ? mileageStatus.value === InputStatus.VALID
    : true
  const circleOfDriversValid = formCircleOfDriversChecked.value
    ? circleOfDriversStatus.value === InputStatus.VALID
    : true
  const insuranceCoverValid = formInsuranceCoverChecked.value
    ? insuranceCoverStatus.value === InputStatus.VALID
    : true
  const isOneFormChecked =
    formMileageChecked.value ||
    formCircleOfDriversChecked.value ||
    formInsuranceCoverChecked.value

  return (
    isOneFormChecked &&
    dataChanged &&
    mileageValid &&
    circleOfDriversValid &&
    insuranceCoverValid
  )
}

const showRecalculateButton = computed(() => {
  const didNumbersOfFormsChanged = numberOfFormsChecked.value !== getHowManyFormChecked()
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  numberOfFormsChecked.value = getHowManyFormChecked()

  let isInsuranceCoverCheckedNew = false
  isInsuranceCoverCheckedNew = getIfInsuranceCoverStateChanged(isInsuranceCoverCheckedNew)
  return (
    !dataChanged.value ||
    formsRecalculateNeeded.value ||
    didNumbersOfFormsChanged ||
    isInsuranceCoverCheckedNew
  )
})

function getInsuranceCoverRequestData(): any {
  if (!requestNew.value || !requestNew.value.insuranceCover) return {}
  return {
    comprehensiveCover:
      requestNew.value.insuranceCover.comprehensiveCover !==
      requestOld.value.insuranceCover.comprehensiveCover
        ? requestNew.value.insuranceCover.comprehensiveCover
        : undefined,
    deductible: !areTwoObjectsWithouthGetterSettersEqual(
      requestNew.value.insuranceCover.deductible,
      requestOld.value.insuranceCover.deductible
    )
      ? requestNew.value.insuranceCover.deductible
      : undefined,
    oldExtras: requestNew.value.insuranceCover.oldExtras,
    newExtras: requestNew.value.insuranceCover.newExtras,
    liabilityBasicDiscountYear: requestNew.value.insuranceCover.liabilityBasicDiscountYear
  }
}

function mileageStatusEvent(status: InputStatus): void {
  mileageStatus.value = status
}

function newMileageStatusEvent(status: InputStatus): void {
  newMileageInputStatus.value = status
}

function circleOfDriversStatusEvent(status: InputStatus): void {
  circleOfDriversStatus.value = status
}

function insuranceCoverStatusEvent(status: InputStatus): void {
  insuranceCoverStatus.value = status
}

function extrasChanged(): void {
  didCalculationDataChange.value = true
}

function getHowManyFormChecked(): number {
  return [
    formCircleOfDriversChecked.value,
    formMileageChecked.value,
    formInsuranceCoverChecked.value
  ].filter((form) => form).length
}

function getHaftpflichtLeistungsExtras(haftpflichtExtras: HaftpflichtExtras): string[] {
  const keys = Object.keys(haftpflichtExtras)
  const arr: string[] = []
  keys.forEach((key) => {
    if (haftpflichtExtras[key as keyof HaftpflichtExtras]) {
      arr.push(t(`insuranceCover.extras.haftpflicht.${key}`))
    }
  })
  return arr
}

function getKaskoLeistungsExtras(kaskoExtras: KaskoExtras | undefined): string[] {
  if (kaskoExtras === undefined) {
    return []
  }
  const keys = Object.keys(kaskoExtras)
  const arr: string[] = []
  keys.forEach((key) => {
    if (kaskoExtras[key as keyof KaskoExtras]) {
      arr.push(t(`insuranceCover.extras.kasko.${key}`))
    }
  })
  return arr
}

defineExpose({ formSubmit })

async function formSubmit(): Promise<void> {
  if (formsValidAndCalculated()) {
    emit('formSubmitting', true)

    try {
      const success = await submitMotorContractChange(
        props.partnerProxyId,
        getMotorContractCoverageChangeRequest()
      )

      emit('formShowResponse', true, success)
    } catch (_error) {
      emit('formShowResponse', true, false)
    } finally {
      emit('formSubmitting', false)
    }
  }
}

function setLeistungsextraTableData(
  rows: string[][],
  leistungsextrasListOld: string,
  leistungsextrasChanged: boolean,
  leistungsextrasListNew: string,
  lengthOfNewLexList: number
) {
  if (
    requestOld.value.insuranceCover.comprehensiveCover !==
    requestNew.value.insuranceCover.comprehensiveCover
  ) {
    rows.push([
      t('motorCockpitForm.leistungsextras.title'),
      formInsuranceCoverChecked.value ? leistungsextrasListOld : '',
      lengthOfNewLexList !== 0 ? leistungsextrasListNew : 'nicht wählbar'
    ])
  } else {
    rows.push([
      t('motorCockpitForm.leistungsextras.title'),
      formInsuranceCoverChecked.value ? leistungsextrasListOld : '',
      leistungsextrasChanged ? leistungsextrasListNew : 'nicht geändert'
    ])
  }
}

function getIfInsuranceCoverStateChanged(isInsuranceCoverCheckedNew: boolean): boolean {
  if (insuranceCoverStateChanged.value) {
    insuranceCoverStateCounter.value--
    if (insuranceCoverStateCounter.value === 0) {
      isInsuranceCoverCheckedNew = true
      insuranceCoverStateChanged.value = false
    }
  }
  return isInsuranceCoverCheckedNew
}

function formatVehicleUsage(request: MotorContractCoverageChange): string {
  return '(' + t(`circleOfDrivers.values.${request.circleOfDrivers.vehicleUsage}`) + ')'
}

function scrollToTop() {
  globalThis.scrollTo({ top: 100 })
}

function updatePremium(value: number): void {
  premium.value = value
  didCalculationDataChange.value = false
  requestCalculated.value = JSON.parse(JSON.stringify(requestNew.value))
}

function updateFormMileageChecked(value: boolean): void {
  formMileageChecked.value = value
}

function updateCircleOfDriversChecked(value: boolean): void {
  formCircleOfDriversChecked.value = value
}

function updateFormInsuranceCoverChecked(value: boolean): void {
  formInsuranceCoverChecked.value = value
}

function resetData(): void {
  requestNew.value = JSON.parse(JSON.stringify(requestOld.value))
  requestCalculated.value = JSON.parse(JSON.stringify(requestOld.value))
  formId.value++
  premium.value = 0
  emit('formsValid', false)
}
</script>

<style lang="scss" scoped>
.pb-baseline {
  padding-bottom: var(--baseline);
}

.pb-baseline2 {
  padding-bottom: calc(var(--baseline) * 2);
}

.margin-calculate {
  margin-top: calc(var(--baseline) * 2);
}

.mb-2 {
  margin-bottom: 2rem;
}

@media (min-width: 600px) {
  .form {
    margin-left: 39px;
    margin-right: 44.5px;
  }
}

.form {
  margin-bottom: 2rem;
}

.card-no-padding :deep(.card__header:not(.modal .card__header)),
.card-no-padding :deep(.card__content:not(.modal .card__content)),
.card-no-padding :deep(.card__divider),
.card-no-padding :deep(.card__footer) {
  padding: 0;
  margin: 0;
}

.card-no-padding :deep(.card__header:not(.modal .card__header)) {
  padding-top: 2rem;
}

.change-table :deep(.is-styled) {
  overflow: auto;
}

.change-table :deep(table) {
  min-width: 800px;
}

@media (min-width: 1362px) {
  .change-table {
    margin-left: 39px;
    margin-right: 44.5px;
  }
}
</style>
